// src/components/ChartRenderer.tsx
//
// Renders a small JSON chart spec (produced by the model in a
// ```chart-json fence) to a Chart.js canvas. Used by CodeBlock.
//
// Expected JSON shape:
// {
//   "type": "bar" | "line" | "pie" | "doughnut",
//   "title": "optional title",
//   "labels": ["Jan", "Feb", "Mar"],
//   "datasets": [{ "label": "Revenue", "data": [10, 20, 30] }]
// }
import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';

interface Props {
  code: string; // raw JSON string
}

export default function ChartRenderer({ code }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let spec: any;
    try {
      spec = JSON.parse(code);
    } catch {
      setError('Invalid chart data (not valid JSON).');
      return;
    }

    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    try {
      const type = spec.type || 'bar';
      const isPieLike = type === 'pie' || type === 'doughnut';

      chartRef.current = new Chart(canvasRef.current, {
        type,
        data: {
          labels: spec.labels || [],
          datasets: spec.datasets || [{ label: spec.label || 'Data', data: spec.data || [] }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: { display: !!spec.title, text: spec.title || '', color: '#e5e7eb' },
            legend: { labels: { color: '#e5e7eb' } },
          },
          scales: isPieLike ? {} : {
            x: { ticks: { color: '#9ca3af' }, grid: { color: 'rgba(255,255,255,0.06)' } },
            y: { ticks: { color: '#9ca3af' }, grid: { color: 'rgba(255,255,255,0.06)' } },
          },
        },
      });
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Could not render this chart.');
    }

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [code]);

  if (error) {
    return (
      <div className="diagram-error">
        <p>⚠ Couldn't render this chart: {error}</p>
        <pre>{code}</pre>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} />
    </div>
  );
}